import { useQuery } from '@tanstack/react-query'
import { ExchangeBadge } from '@/components/common/ExchangeBadge'
import { getCoinTradeSeconds } from '@/lib/api'

const quantity = new Intl.NumberFormat('en-US', { maximumSignificantDigits: 6 })
const clock = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC',
})

export function CoinLiveTrades({ ticker, enabled }: { ticker: string; enabled: boolean }) {
  const query = useQuery({
    queryKey: ['coin-trade-seconds', ticker],
    queryFn: ({ signal }) => getCoinTradeSeconds(ticker, signal),
    enabled,
    refetchInterval: 1000,
    refetchIntervalInBackground: false,
    retry: 1,
  })
  const rows = (query.data?.venues ?? []).flatMap(venue => {
    const point = venue.points.at(-1)
    return point ? [{ ...venue, point }] : []
  })
  return (
    <div className="mb-4 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Live trades · 1 second</span>
        <span className="text-xs text-muted-foreground">UTC</span>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        Latest recorded second for each market in the last 10 seconds.
      </p>
      {query.isError ? (
        <p className="text-xs text-destructive">Live trades are temporarily unavailable.</p>
      ) : query.data?.detail_available === false ? (
        <p className="text-xs text-muted-foreground">One-second history is not available yet.</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {query.isPending ? 'Loading live trades…' : 'No trades recorded in this window. The market may be quiet or its feed unavailable.'}
        </p>
      ) : (
        <div className="max-h-64 overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr><th className="text-left">Exchange / market</th><th className="text-right">Second</th>
                <th className="text-right">Bought ({ticker})</th><th className="text-right">Sold ({ticker})</th>
                <th className="text-right">Events</th></tr>
            </thead>
            <tbody>
              {rows.map(({ exchange, market_type: market, point }) => {
                const unitKnown = point.quantity_unit === 'base_normalized'
                  && !point.quality_flags.includes('mixed_quantity_unit')
                return (
                  <tr key={`${exchange}:${market}`} className="border-t tabular-nums">
                    <td className="py-1.5"><span className="inline-flex items-center gap-1.5">
                      <ExchangeBadge exchange={exchange} /><span className="text-muted-foreground">{market}</span>
                    </span></td>
                    <td className="text-right" title={point.interval_elapsed ? 'Elapsed second; late trades may still arrive' : 'Current partial second'}>
                      {clock.format(new Date(point.timestamp * 1000))}{point.interval_elapsed ? '' : '…'}
                    </td>
                    <td className="text-right text-green-500">{unitKnown ? quantity.format(point.buy_total_qty) : '—'}</td>
                    <td className="text-right text-red-500">{unitKnown ? quantity.format(point.sell_total_qty) : '—'}</td>
                    <td className="text-right" title="Normalized trade events; an exchange may aggregate several fills into one event">
                      {point.buy_total_count + point.sell_total_count}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
